import { motion, AnimatePresence } from 'framer-motion';
import { useMemo, useRef, useState } from 'react';
import { X, Users, ArrowRightLeft, Building2, CheckCircle2, Download } from 'lucide-react';
import { generatePDFSimple } from '../services/pdfGenerator';

import { OfficialOwnershipContent } from './OfficialOwnershipBZ';

interface ActionnariatScenariosModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type Scenario = 'mixte' | 'toutOctets' | 'structureCible';
type CouleurStructure = 'jaune' | 'bleu' | 'orange';
type StructureAchat = 'Octets' | 'MegaOctets' | 'Gestion perso' | 'CIE #' | 'Gestion YM';

interface Scenario3Holding {
  name: string;
  percentage: number;
  nature: string;
  detailLabel?: string;
  members?: Scenario3VehicleMember[];
}

interface Scenario3Transaction {
  id: number;
  vendeur: string;
  acheteur: string;
  transaction: string;
  percentage: number;
}

interface Scenario3VehicleMember {
  name: string;
  percentage: number;
  note?: string;
}

interface Scenario3FinalHolding {
  name: string;
  percentage: number;
  variation: string;
  detailLabel?: string;
  detailNote?: string;
  members?: Scenario3VehicleMember[];
}

interface Scenario3ConsolidatedHolding {
  name: string;
  percentage: number;
}

const actionnairesInitiaux: Record<string, number> = {
  'Dany Bourassa': 33.49,
  'Francois Delisle': 13.52,
  'Francois Zicat': 12.49,
  'Yannick Moreau': 8.0,
  'Eric Hains': 8.5,
  'Vincent Bourassa': 5.5,
  'Francois Bouchard': 3.5,
  'Francis Marquis': 3.5,
  'Antoine Giroux': 2.0,
  'Richard Delisle': 3.5,
  'Jean-Pierre Leclerc': 1.0,
  'Jerome Martel': 2.0,
};

const vendeursParScenario: Record<Scenario, { name: string; montant: number }[]> = {
  mixte: [
    { name: 'Francois Bouchard', montant: 3.5 },
    { name: 'Francois Zicat', montant: 7.49 },
    { name: 'Dany Bourassa', montant: 2.0 },
  ],
  toutOctets: [
    { name: 'Francois Bouchard', montant: 3.5 },
    { name: 'Francois Zicat', montant: 7.49 },
    { name: 'Dany Bourassa', montant: 2.0 },
  ],
  structureCible: [],
};

const acheteurs = [
  { name: 'Francis Marquis', montant: 1.5, couleur: 'jaune' as CouleurStructure },
  { name: 'Richard Delisle', montant: 2.0, couleur: 'jaune' as CouleurStructure },
  { name: 'Antoine Giroux', montant: 3.0, couleur: 'bleu' as CouleurStructure },
  { name: 'Louis-Alexandre Hébert', montant: 3.0, couleur: 'bleu' as CouleurStructure },
  { name: 'Jean-Michel Caouette', montant: 2.0, couleur: 'bleu' as CouleurStructure },
  { name: 'Jean-Pierre Leclerc', montant: 2.49, couleur: 'bleu' as CouleurStructure },
  { name: 'Yannick Moreau', montant: 4.0, couleur: 'bleu' as CouleurStructure },
];

const scenario3Avant: Scenario3Holding[] = [
  { name: 'Dany Bourassa / Gestion DB', percentage: 33.49, nature: '' },
  { name: 'Gestion Octet', percentage: 21.0, nature: '', detailLabel: 'Composition économique (% BZ)', members: [
    { name: 'François Délisle / 9410', percentage: 3.5 },
    { name: 'Vincent Bourassa / Gestion VBou', percentage: 3.5 },
    { name: 'Francis Marquis / Gestion Nibble', percentage: 3.5 },
    { name: 'François Bouchard / Gestion B&D', percentage: 3.5 },
    { name: 'Éric Hains / Gestion EH', percentage: 3.5 },
    { name: 'Richard Délisle', percentage: 3.5 },
  ] },
  { name: 'François Zicat / Gestion FZ', percentage: 17.49, nature: '' },
  { name: 'Gestion Megaoctet', percentage: 14.0, nature: '', detailLabel: 'Composition économique (% BZ)', members: [
    { name: 'Yannick Moreau / Gestion YM', percentage: 5.0 },
    { name: 'Éric Hains / Gestion EH', percentage: 5.0 },
    { name: 'Antoine Giroux', percentage: 2.0 },
    { name: 'Vincent Bourassa / Gestion VBou', percentage: 2.0 },
  ] },
  { name: 'François Délisle / 9410', percentage: 10.02, nature: '' },
  { name: 'Yannick Moreau / Gestion YM', percentage: 3.0, nature: '' },
  { name: 'Jean-Pierre Leclerc', percentage: 1.0, nature: '' },
];

const scenario3Transactions: Scenario3Transaction[] = [
  { id: 1, vendeur: 'François Zicat / Gestion FZ', acheteur: 'CIE #', transaction: 'Vente directe d’actions BZ', percentage: 10.49 },
  { id: 2, vendeur: 'François Zicat / Gestion FZ', acheteur: 'Richard Délisle', transaction: 'Vente directe d’actions BZ', percentage: 2.0 },
  { id: 3, vendeur: 'François Bouchard / Gestion B&D', acheteur: 'CIE #', transaction: 'Participation dans Gestion Octet équivalant à BZ', percentage: 3.5 },
];

const scenario3Apres: Scenario3FinalHolding[] = [
  { name: 'Dany Bourassa / Gestion DB', percentage: 33.49, variation: '—' },
  {
    name: 'Gestion Octet',
    percentage: 21.0,
    variation: '—',
    detailLabel: 'Composition économique (% BZ)',
    members: [
      { name: 'François Délisle / 9410', percentage: 3.5 },
      { name: 'Vincent Bourassa / Gestion VBou', percentage: 3.5 },
      { name: 'Francis Marquis / Gestion Nibble', percentage: 3.5 },
      { name: 'CIE #', percentage: 3.5 },
      { name: 'Éric Hains / Gestion EH', percentage: 3.5 },
      { name: 'Richard Délisle', percentage: 3.5 },
    ],
  },
  {
    name: 'Gestion Megaoctet',
    percentage: 14.0,
    variation: '—',
    detailLabel: 'Composition économique (% BZ)',
    members: [
      { name: 'Yannick Moreau / Gestion YM', percentage: 5.0 },
      { name: 'Éric Hains / Gestion EH', percentage: 5.0 },
      { name: 'Antoine Giroux', percentage: 2.0 },
      { name: 'Vincent Bourassa / Gestion VBou', percentage: 2.0 },
    ],
  },
  {
    name: 'François Délisle / 9410',
    percentage: 10.02,
    variation: '—',
  },
  {
    name: 'CIE #',
    percentage: 10.49,
    variation: '+13.99% éco.',
    detailLabel: 'Composition économique (% BZ)',
    detailNote: 'Total économique : 13.99 % (10.49 % direct BZ + 3.50 % via Gestion Octet)',
    members: [
      { name: 'Louis-Alexandre Hébert', percentage: 3.0 },
      { name: 'Antoine Giroux', percentage: 3.0 },
      { name: 'Jean-Pierre Leclerc', percentage: 2.49 },
      { name: 'Jean-Michel Caouette', percentage: 2.0 },
      { name: 'Francis Marquis / Gestion Nibble', percentage: 1.5 },
      { name: 'Mélissa Pelletier', percentage: 1.0 },
      { name: 'Eric Durand', percentage: 1.0 },
    ],
  },
  { name: 'François Zicat / Gestion FZ', percentage: 5.0, variation: '-12.49%' },
  { name: 'Yannick Moreau / Gestion YM', percentage: 3.0, variation: '—' },
  { name: 'Richard Délisle', percentage: 2.0, variation: '+2.00%' },
  { name: 'Jean-Pierre Leclerc', percentage: 1.0, variation: '—' },
];

const scenario3ConsolidatedAvant: Scenario3ConsolidatedHolding[] = [
  { name: 'Dany Bourassa', percentage: 33.49 },
  { name: 'François Zicat', percentage: 17.49 },
  { name: 'François Délisle', percentage: 13.52 },
  { name: 'Éric Hains', percentage: 8.5 },
  { name: 'Yannick Moreau', percentage: 8.0 },
  { name: 'Vincent Bourassa', percentage: 5.5 },
  { name: 'Francis Marquis', percentage: 3.5 },
  { name: 'François Bouchard', percentage: 3.5 },
  { name: 'Richard Délisle', percentage: 3.5 },
  { name: 'Antoine Giroux', percentage: 2.0 },
  { name: 'Jean-Pierre Leclerc', percentage: 1.0 },
];

const scenario3ConsolidatedApres: Scenario3ConsolidatedHolding[] = [
  { name: 'Dany Bourassa', percentage: 33.49 },
  { name: 'François Délisle', percentage: 13.52 },
  { name: 'Éric Hains', percentage: 8.5 },
  { name: 'Yannick Moreau', percentage: 8.0 },
  { name: 'François Zicat', percentage: 5.0 },
  { name: 'Vincent Bourassa', percentage: 5.5 },
  { name: 'Antoine Giroux', percentage: 5.0 },
  { name: 'Francis Marquis', percentage: 5.0 },
  { name: 'Richard Délisle', percentage: 5.5 },
  { name: 'Jean-Pierre Leclerc', percentage: 3.49 },
  { name: 'Louis-Alexandre Hébert', percentage: 3.0 },
  { name: 'Jean-Michel Caouette', percentage: 2.0 },
  { name: 'Mélissa Pelletier', percentage: 1.0 },
  { name: 'Eric Durand', percentage: 1.0 },
];

function getStructure(couleur: CouleurStructure, currentScenario: Scenario, acheteurName?: string): StructureAchat {
  if (currentScenario === 'structureCible') {
    if (acheteurName === 'Richard Delisle') return 'Octets';
    if (acheteurName === 'Yannick Moreau') return 'Gestion YM';
    return 'CIE #';
  }
  if (currentScenario === 'toutOctets') {
    return acheteurName === 'Richard Delisle' ? 'Octets' : 'CIE #';
  }
  if (couleur === 'jaune') return 'Octets';
  if (couleur === 'bleu') return 'MegaOctets';
  return 'Gestion perso';
}

function calculerScenario(scenario: Scenario) {
  const apres = { ...actionnairesInitiaux };

  vendeursParScenario[scenario].forEach((vendeur) => {
    apres[vendeur.name] = (apres[vendeur.name] || 0) - vendeur.montant;
  });

  acheteurs.forEach((acheteur) => {
    apres[acheteur.name] = (apres[acheteur.name] || 0) + acheteur.montant;
  });

  return Object.entries(apres)
    .map(([name, percentage]) => ({
      name,
      percentage: parseFloat(percentage.toFixed(2)),
      initial: parseFloat((actionnairesInitiaux[name] || 0).toFixed(2)),
      delta: parseFloat((percentage - (actionnairesInitiaux[name] || 0)).toFixed(2)),
    }))
    .filter((a) => a.name !== 'Jerome Martel' && a.percentage > 0)
    .sort((a, b) => b.percentage - a.percentage);
}

function getStructureBadgeClasses(structure: string) {
  if (structure === 'MegaOctets') return 'bg-blue-100 text-blue-800 border-blue-200';
  if (structure === 'Octets') return 'bg-amber-100 text-amber-800 border-amber-200';
  if (structure === 'CIE #') return 'bg-violet-100 text-violet-800 border-violet-200';
  if (structure === 'Gestion YM') return 'bg-emerald-100 text-emerald-800 border-emerald-200';
  return 'bg-orange-100 text-orange-800 border-orange-200';
}

export default function ActionnariatScenariosModal({ isOpen, onClose }: ActionnariatScenariosModalProps) {
  const [official, setOfficial] = useState(false);
  const [scenario, setScenario] = useState<Scenario>('mixte');
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const exportRef = useRef<HTMLDivElement | null>(null);
  const printRef = useRef<HTMLDivElement | null>(null);

  const actionnariatFinal = useMemo(() => calculerScenario(scenario), [scenario]);
  const vendeurs = vendeursParScenario[scenario];
  const isScenario3 = scenario === 'structureCible';
  const scenario3StructureAvantTotal = useMemo(
    () => scenario3Avant.reduce((acc, curr) => acc + curr.percentage, 0),
    [],
  );
  const scenario3StructureApresTotal = useMemo(
    () => scenario3Apres.reduce((acc, curr) => acc + curr.percentage, 0),
    [],
  );
  const scenario3ConsolidatedAvantTotal = useMemo(
    () => scenario3ConsolidatedAvant.reduce((acc, curr) => acc + curr.percentage, 0),
    [],
  );
  const scenario3ConsolidatedApresTotal = useMemo(
    () => scenario3ConsolidatedApres.reduce((acc, curr) => acc + curr.percentage, 0),
    [],
  );

  const totalFinal = useMemo(
    () => actionnariatFinal.reduce((acc, curr) => acc + curr.percentage, 0),
    [actionnariatFinal],
  );

  const totalVendeurs = useMemo(
    () => vendeurs.reduce((acc, curr) => acc + curr.montant, 0),
    [vendeurs],
  );

  const totalAcheteurs = useMemo(
    () => acheteurs.reduce((acc, curr) => acc + curr.montant, 0),
    [],
  );

  const repartitionStructures = useMemo(() => {
    const groupes: Record<StructureAchat, { total: number; items: typeof acheteurs }> = {} as Record<StructureAchat, { total: number; items: typeof acheteurs }>;

    acheteurs.forEach((acheteur) => {
      const structure = getStructure(acheteur.couleur, scenario, acheteur.name);
      if (!groupes[structure]) groupes[structure] = { total: 0, items: [] };
      groupes[structure].items.push(acheteur);
      groupes[structure].total += acheteur.montant;
    });

    return ['Octets', 'MegaOctets', 'CIE #', 'Gestion perso']
      .filter((structure): structure is StructureAchat => Boolean(groupes[structure as StructureAchat]))
      .map((structure) => {
        const groupe = groupes[structure];

        return {
          structure,
          total: groupe.total,
          items: groupe.items,
          nestedGroup: null,
        };
      });
  }, [scenario]);

  const handleExportPdf = async () => {
    if (!printRef.current || isExportingPdf) return;

    try {
      setIsExportingPdf(true);
      const node = printRef.current;
      const exportId = `actionnariat-export-${scenario}`;
      const previousId = node.id;
      node.id = exportId;

      await new Promise((resolve) => setTimeout(resolve, 120));
      await generatePDFSimple(exportId, `actionnariat-${scenario}.pdf`);

      node.id = previousId;
    } catch (error) {
      console.error('Erreur export PDF actionnariat:', error);
      window.alert("L'export PDF a échoué. Recharge la page et réessaie.");
    } finally {
      if (printRef.current?.id?.startsWith('actionnariat-export-')) {
        printRef.current.id = '';
      }
      setIsExportingPdf(false);
    }
  };

  return (
    <>
      <div className="fixed left-[-10000px] top-0 z-[-1] opacity-0 pointer-events-none">
        <div
          ref={printRef}
          className="w-[1020px] bg-slate-50 p-5 text-slate-900"
        >
          <div className="rounded-[22px] border border-slate-200 bg-white shadow-xl overflow-hidden">
            <div className="border-b border-slate-200 bg-gradient-to-r from-indigo-700 via-blue-700 to-cyan-600 px-6 py-5 text-white">
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15 ring-1 ring-white/20 backdrop-blur-sm">
                  <Users className="h-6 w-6" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold tracking-tight">Scénarios de structure d'actionnariat</h2>
                  <p className="mt-1 text-sm text-blue-100">
                    Vue simple et claire de la transaction, de la structure d'achat et du résultat final.
                  </p>
                </div>
              </div>
            </div>

            <div className="p-5 space-y-5">
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-2xl border border-indigo-100 bg-indigo-50 p-4">
                  <div className="mb-2 text-xs font-bold uppercase tracking-wide text-indigo-700">Scénario actif</div>
                  <p className="text-sm font-semibold text-slate-900">
                    {scenario === 'mixte'
                      ? 'Bleus via MegaOctets, jaunes via Octets.'
                      : scenario === 'toutOctets'
                        ? 'Richard via Octets; les autres acheteurs via CIE #.'
                        : 'CIE # reçoit 13,99 % économique; Richard achète 2,00 % personnellement.'}
                  </p>
                </div>
                <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4">
                  <div className="mb-2 text-xs font-bold uppercase tracking-wide text-emerald-700">Transaction</div>
                  <p className="text-2xl font-bold text-slate-900">{isScenario3 ? scenario3Transactions.length : acheteurs.length}</p>
                  <p className="text-sm text-slate-600">{isScenario3 ? 'mouvements structurants' : `acheteurs · ${vendeurs.length} vendeurs`}</p>
                </div>
                <div className="rounded-2xl border border-amber-100 bg-amber-50 p-4">
                  <div className="mb-2 text-xs font-bold uppercase tracking-wide text-amber-700">Validation totale</div>
                  <p className="text-2xl font-bold text-slate-900">{isScenario3 ? `${scenario3StructureApresTotal.toFixed(2)}%` : `${totalFinal.toFixed(2)}%`}</p>
                  <p className="text-sm text-slate-600">{isScenario3 ? 'structure principale consolidée' : 'actionnariat final calculé'}</p>
                </div>
              </div>

              {isScenario3 ? (
                <div className="space-y-5">
                  <div className="grid grid-cols-12 gap-4">
                    <div className="col-span-4 rounded-2xl border border-sky-200 bg-sky-50 p-4 shadow-sm">
                      <div className="mb-4 flex items-center justify-between gap-4">
                        <h4 className="text-base font-bold text-sky-900">Structure avant transaction</h4>
                        <span className="rounded-full border border-sky-200 bg-white px-3 py-1 text-xs font-bold text-sky-700">{scenario3Avant.length} blocs · {scenario3StructureAvantTotal.toFixed(2)}%</span>
                      </div>
                      <div className="space-y-2">
                        {[...scenario3Avant]
                          .sort((a, b) => (b.percentage - a.percentage) || a.name.localeCompare(b.name, 'fr'))
                          .map((item) => (
                            <div key={`print-before-${item.name}`} className="rounded-xl border border-sky-100 bg-white px-3 py-3">
                              <div className="grid grid-cols-[minmax(0,1fr)_76px] gap-3">
                                <div>
                                  <p className="text-[13px] font-semibold text-slate-900">{item.name}</p>
                                  {item.nature ? <p className="text-[11px] text-slate-500">{item.nature}</p> : null}
                                </div>
                                <span className="text-right text-[13px] font-bold text-sky-700">{item.percentage.toFixed(2)}%</span>
                              </div>
                              {item.members?.length ? (
                                <div className="mt-3 rounded-xl border border-dashed border-sky-200 bg-sky-50/70 px-3 py-3">
                                  <p className="mb-2 text-[10px] font-bold uppercase tracking-wide text-sky-700">{item.detailLabel}</p>
                                  <div className="space-y-1.5">
                                    {[...item.members]
                                      .sort((a, b) => (b.percentage - a.percentage) || a.name.localeCompare(b.name, 'fr'))
                                      .map((member) => (
                                        <div key={`print-before-${item.name}-${member.name}`} className="flex items-center justify-between gap-3 text-[11px] text-slate-700">
                                          <span className="truncate">{member.name}</span>
                                          <span className="font-semibold text-sky-700">{member.percentage.toFixed(2)}%</span>
                                        </div>
                                      ))}
                                  </div>
                                </div>
                              ) : null}
                            </div>
                          ))}
                      </div>
                      <p className="mt-4 text-[11px] text-sky-800">Le 2 % précédemment détenu par Jérôme Martel est déjà intégré à Dany Bourassa / Gestion DB.</p>
                    </div>

                    <div className="col-span-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 shadow-sm">
                      <div className="mb-4 flex items-center justify-between gap-4">
                        <h4 className="text-base font-bold text-emerald-900">Transactions structurantes</h4>
                        <span className="rounded-full border border-emerald-200 bg-white px-3 py-1 text-xs font-bold text-emerald-700">{scenario3Transactions.length} mouvements</span>
                      </div>
                      <div className="space-y-3">
                        {scenario3Transactions.map((item) => (
                          <div key={`print-tx-${item.id}`} className="rounded-xl border border-emerald-100 bg-white px-3 py-3">
                            <div className="mb-1 flex items-start justify-between gap-3">
                              <p className="text-[13px] font-semibold text-slate-900">#{item.id} · {item.vendeur}</p>
                              <span className="text-[13px] font-bold text-emerald-700">{item.percentage.toFixed(2)}%</span>
                            </div>
                            <p className="text-[12px] text-slate-700">→ {item.acheteur}</p>
                            <p className="mt-1 text-[11px] text-slate-500">{item.transaction}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="col-span-4 rounded-2xl border border-indigo-200 bg-indigo-50 p-4 shadow-sm">
                      <div className="mb-4 flex items-center justify-between gap-4">
                        <h4 className="text-base font-bold text-indigo-900">Structure après transaction</h4>
                        <span className="rounded-full border border-indigo-200 bg-white px-3 py-1 text-xs font-bold text-indigo-700">{scenario3Apres.length} blocs</span>
                      </div>
                      <div className="space-y-2">
                        {[...scenario3Apres]
                          .sort((a, b) => (b.percentage - a.percentage) || a.name.localeCompare(b.name, 'fr'))
                          .map((item) => (
                            <div key={`print-after-${item.name}`} className="rounded-xl border border-indigo-100 bg-white px-3 py-3">
                              <div className="grid grid-cols-[minmax(0,1fr)_74px_76px] items-center gap-2">
                                <span className="text-[13px] font-semibold text-slate-900">{item.name}</span>
                                <span className={`text-right text-[12px] font-semibold ${item.variation.startsWith('+') ? 'text-green-700' : item.variation.startsWith('-') ? 'text-red-700' : 'text-slate-400'}`}>{item.variation}</span>
                                <span className="text-right text-[13px] font-bold text-indigo-700">{item.percentage.toFixed(2)}%</span>
                              </div>
                              {item.members?.length ? (
                                <div className="mt-3 rounded-xl border border-dashed border-indigo-200 bg-indigo-50/70 px-3 py-3">
                                  <p className="mb-2 text-[10px] font-bold uppercase tracking-wide text-indigo-700">{item.detailLabel}</p>
                                  <div className="space-y-1.5">
                                    {[...item.members]
                                      .sort((a, b) => (b.percentage - a.percentage) || a.name.localeCompare(b.name, 'fr'))
                                      .map((member) => (
                                        <div key={`print-after-${item.name}-${member.name}`} className="flex items-center justify-between gap-3 text-[11px] text-slate-700">
                                          <span className="truncate">{member.name}</span>
                                          <span className="font-semibold text-indigo-700">{member.percentage.toFixed(2)}%</span>
                                        </div>
                                      ))}
                                  </div>
                                  {item.detailNote ? <p className="mt-2 text-[10px] text-indigo-700">{item.detailNote}</p> : null}
                                </div>
                              ) : null}
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                      <div className="mb-3 flex items-center justify-between gap-3">
                        <h4 className="text-base font-bold text-slate-900">Actionnaires finaux avant</h4>
                        <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-700">{scenario3ConsolidatedAvantTotal.toFixed(2)}%</span>
                      </div>
                      <div className="space-y-2">
                        {[...scenario3ConsolidatedAvant]
                          .sort((a, b) => (b.percentage - a.percentage) || a.name.localeCompare(b.name, 'fr'))
                          .map((item) => (
                            <div key={`consolidated-before-${item.name}`} className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50 px-3 py-2 text-[12px]">
                              <span className="font-medium text-slate-900">{item.name}</span>
                              <span className="font-bold text-slate-700">{item.percentage.toFixed(2)}%</span>
                            </div>
                          ))}
                      </div>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                      <div className="mb-3 flex items-center justify-between gap-3">
                        <h4 className="text-base font-bold text-slate-900">Actionnaires finaux après</h4>
                        <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-700">{scenario3ConsolidatedApresTotal.toFixed(2)}%</span>
                      </div>
                      <div className="space-y-2">
                        {[...scenario3ConsolidatedApres]
                          .sort((a, b) => (b.percentage - a.percentage) || a.name.localeCompare(b.name, 'fr'))
                          .map((item) => (
                            <div key={`consolidated-after-${item.name}`} className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50 px-3 py-2 text-[12px]">
                              <span className="font-medium text-slate-900">{item.name}</span>
                              <span className="font-bold text-slate-700">{item.percentage.toFixed(2)}%</span>
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-12 gap-4">
                  <div className="col-span-3 space-y-3">
                    <div className="rounded-2xl border border-red-200 bg-red-50 p-4">
                      <div className="mb-3 flex items-center justify-between gap-3">
                        <h4 className="text-sm font-bold uppercase tracking-wide text-red-700">Vendeurs</h4>
                        <span className="rounded-full border border-red-200 bg-white px-3 py-1 text-xs font-bold text-red-700">
                          Total {totalVendeurs.toFixed(2)}%
                        </span>
                      </div>
                      <div className="space-y-3">
                        {vendeurs.map((vendeur) => (
                          <div key={`print-seller-${vendeur.name}`} className="flex items-center justify-between rounded-xl bg-white px-3 py-2.5 text-[13px]">
                            <span className="font-medium text-slate-900">{vendeur.name}</span>
                            <span className="font-bold text-red-700">-{vendeur.montant.toFixed(2)}%</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="rounded-2xl border border-green-200 bg-green-50 p-4">
                      <div className="mb-3 flex items-center justify-between gap-3">
                        <h4 className="text-sm font-bold uppercase tracking-wide text-green-700">Acheteurs</h4>
                        <span className="rounded-full border border-green-200 bg-white px-3 py-1 text-xs font-bold text-green-700">
                          Total {totalAcheteurs.toFixed(2)}%
                        </span>
                      </div>
                      <div className="space-y-3">
                        {acheteurs.map((acheteur) => (
                          <div key={`print-buyer-${acheteur.name}`} className="flex items-center justify-between rounded-xl bg-white px-3 py-2.5 text-[13px]">
                            <span className="font-medium text-slate-900">{acheteur.name}</span>
                            <span className="font-bold text-green-700">+{acheteur.montant.toFixed(2)}%</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="col-span-4">
                    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                      <h4 className="mb-4 text-base font-bold text-slate-900">Structure d'achat</h4>
                      <div className="space-y-4">
                        {repartitionStructures.map((groupe) => (
                          <div key={`print-structure-${groupe.structure}`} className="rounded-2xl border border-slate-200 bg-slate-50 p-3.5">
                            <div className="mb-3 flex items-center justify-between gap-3">
                              <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${getStructureBadgeClasses(groupe.structure)}`}>
                                {groupe.structure}
                              </span>
                              <span className="text-sm font-bold text-slate-900">{groupe.total.toFixed(2)}%</span>
                            </div>
                            <div className="space-y-2">
                              {groupe.items.map((item) => (
                                <div key={`print-structure-item-${item.name}`} className="flex items-center justify-between rounded-xl bg-white px-3 py-2.5 text-[13px]">
                                  <span className="font-medium text-slate-900">{item.name}</span>
                                  <span className="font-semibold text-slate-700">{item.montant.toFixed(2)}%</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="col-span-5">
                    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                      <div className="mb-4 flex items-center justify-between gap-4">
                        <h4 className="text-base font-bold text-slate-900">Actionnariat final</h4>
                        <span className="rounded-full bg-indigo-50 px-3 py-1 text-xs font-bold text-indigo-700">
                          Total {totalFinal.toFixed(2)}%
                        </span>
                      </div>
                      <div className="space-y-2">
                        {actionnariatFinal.map((actionnaire) => (
                          <div key={`print-final-${actionnaire.name}`} className="grid grid-cols-[minmax(0,1fr)_78px_78px] items-center gap-2 rounded-xl border border-slate-100 px-3 py-2.5 text-[13px]">
                            <span className="truncate font-medium text-slate-900">{actionnaire.name}</span>
                            <span className={`text-right font-semibold ${
                              actionnaire.delta > 0 ? 'text-green-700' : actionnaire.delta < 0 ? 'text-red-700' : 'text-slate-400'
                            }`}>
                              {actionnaire.delta > 0 ? '+' : ''}{actionnaire.delta.toFixed(2)}%
                            </span>
                            <span className="text-right font-bold text-indigo-700">{actionnaire.percentage.toFixed(2)}%</span>
                          </div>
                        ))}
                        <div className="mt-3 grid grid-cols-[minmax(0,1fr)_78px_78px] items-center gap-2 rounded-xl bg-slate-100 px-3 py-2.5 text-[13px] font-bold text-slate-900">
                          <span>Total</span>
                          <span className="text-right">—</span>
                          <span className="text-right">{totalFinal.toFixed(2)}%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 md:p-6"
          onClick={onClose}
        >
          <motion.div
            ref={exportRef}
            initial={{ opacity: 0, scale: 0.97, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: 12 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-[92vw] xl:max-w-7xl max-h-[92vh] overflow-hidden rounded-3xl border border-gray-200 bg-gradient-to-br from-gray-50 to-white shadow-2xl"
          >
            <div className="flex items-center justify-between border-b border-gray-200 bg-gradient-to-r from-indigo-700 via-blue-700 to-cyan-600 px-6 py-5 text-white md:px-8">
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15 ring-1 ring-white/20 backdrop-blur-sm">
                  <Users className="h-6 w-6" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold tracking-tight">Scénarios de structure d'actionnariat</h2>
                  <p className="mt-1 text-sm text-blue-100">
                    Vue simple et claire de la transaction, de la structure d'achat et du résultat final.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => official ? window.open('/api/prisme/ca/actionnariat/document', '_blank', 'noopener,noreferrer') : handleExportPdf()}
                  disabled={isExportingPdf}
                  className="inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-white/25 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <Download className="h-4 w-4" />
                  {isExportingPdf ? 'Export...' : 'Export PDF'}
                </button>
                <button
                  onClick={onClose}
                  className="rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div data-export-scroll="true" className="overflow-y-auto max-h-[calc(92vh-88px)] px-6 py-6 md:px-8 md:py-8">
              <div className="mb-6 flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Choix du scénario</h3>
                  <p className="text-sm text-gray-600">Même logique, meilleure lisibilité, sans complexifier la modal.</p>
                </div>
                <div className="grid w-full grid-cols-1 gap-2 rounded-2xl bg-gray-100 p-1.5 shadow-inner sm:grid-cols-2 xl:grid-cols-4">
                  <button
                    onClick={() => { setOfficial(false); setScenario('mixte'); }}
                    className={`w-full rounded-xl px-4 py-3 text-sm font-semibold transition-all ${
                      !official && scenario === 'mixte'
                        ? 'bg-white text-indigo-700 shadow-sm'
                        : 'text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    Scénario 1 · Mixte
                  </button>
                  <button
                    onClick={() => { setOfficial(false); setScenario('toutOctets'); }}
                    className={`w-full rounded-xl px-4 py-3 text-sm font-semibold transition-all ${
                      !official && scenario === 'toutOctets'
                        ? 'bg-white text-indigo-700 shadow-sm'
                        : 'text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    Scénario 2 · Tout Octets
                  </button>
                  <button
                    onClick={() => { setOfficial(false); setScenario('structureCible'); }}
                    className={`w-full rounded-xl px-4 py-3 text-sm font-semibold transition-all ${
                      !official && scenario === 'structureCible'
                        ? 'bg-white text-indigo-700 shadow-sm'
                        : 'text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    Scénario 3 · Structure cible
                  </button>
                  <button onClick={() => setOfficial(true)} className={`w-full rounded-xl px-4 py-3 text-sm font-semibold ${official ? 'bg-emerald-700 text-white shadow-sm' : 'text-emerald-800 hover:bg-emerald-50'}`}>Scénario OFFICIEL FINAL</button>
                </div>
              </div>

              {official ? <OfficialOwnershipContent /> : <>
              <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="rounded-2xl border border-indigo-100 bg-indigo-50 p-4">
                  <div className="mb-2 flex items-center gap-2 text-indigo-700">
                    <CheckCircle2 className="h-4 w-4" />
                    <p className="text-xs font-bold uppercase tracking-wide">Scénario actif</p>
                  </div>
                  <p className="text-sm font-semibold text-gray-900">
                    {scenario === 'mixte'
                      ? 'Bleus via MegaOctets, jaunes via Octets.'
                      : scenario === 'toutOctets'
                        ? 'Richard via Octets; les autres acheteurs via CIE #.'
                        : 'CIE # reçoit 13,99 % économique; Richard achète 2,00 % personnellement.'}
                  </p>
                </div>
                <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4">
                  <div className="mb-2 flex items-center gap-2 text-emerald-700">
                    <ArrowRightLeft className="h-4 w-4" />
                    <p className="text-xs font-bold uppercase tracking-wide">Transaction</p>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{isScenario3 ? scenario3Transactions.length : acheteurs.length}</p>
                  <p className="text-sm text-gray-600">{isScenario3 ? 'mouvements structurants' : `acheteurs · ${vendeurs.length} vendeurs`}</p>
                </div>
                <div className="rounded-2xl border border-amber-100 bg-amber-50 p-4">
                  <div className="mb-2 flex items-center gap-2 text-amber-700">
                    <Building2 className="h-4 w-4" />
                    <p className="text-xs font-bold uppercase tracking-wide">Validation totale</p>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{isScenario3 ? `${scenario3StructureApresTotal.toFixed(2)}%` : `${totalFinal.toFixed(2)}%`}</p>
                  <p className="text-sm text-gray-600">{isScenario3 ? 'structure principale consolidée' : 'actionnariat final calculé'}</p>
                </div>
              </div>

              {isScenario3 ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
                    <div className="xl:col-span-4 rounded-2xl border border-sky-200 bg-sky-50 p-5 shadow-sm">
                      <div className="mb-4 flex items-center justify-between gap-4">
                        <h4 className="text-base font-bold text-sky-900">Structure avant transaction</h4>
                        <span className="rounded-full border border-sky-200 bg-white px-3 py-1 text-xs font-bold text-sky-700">
                          {scenario3Avant.length} blocs · {scenario3StructureAvantTotal.toFixed(2)}%
                        </span>
                      </div>
                      <div className="space-y-2">
                        {[...scenario3Avant]
                          .sort((a, b) => (b.percentage - a.percentage) || a.name.localeCompare(b.name, 'fr'))
                          .map((item) => (
                            <div key={item.name} className="rounded-xl border border-sky-100 bg-white px-4 py-3">
                              <div className="grid grid-cols-[minmax(0,1fr)_80px] gap-3">
                                <div>
                                  <p className="text-sm font-semibold text-gray-900">{item.name}</p>
                                  {item.nature ? <p className="text-xs text-gray-500">{item.nature}</p> : null}
                                </div>
                                <span className="text-right text-sm font-bold text-sky-700">{item.percentage.toFixed(2)}%</span>
                              </div>
                              {item.members?.length ? (
                                <div className="mt-3 rounded-xl border border-dashed border-sky-200 bg-sky-50/70 px-3 py-3">
                                  <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-sky-700">{item.detailLabel}</p>
                                  <div className="space-y-1.5">
                                    {[...item.members]
                                      .sort((a, b) => (b.percentage - a.percentage) || a.name.localeCompare(b.name, 'fr'))
                                      .map((member) => (
                                        <div key={`${item.name}-${member.name}`} className="flex items-center justify-between gap-3 text-[12px] text-gray-700">
                                          <span className="truncate">{member.name}</span>
                                          <span className="font-semibold text-sky-700">{member.percentage.toFixed(2)}%</span>
                                        </div>
                                      ))}
                                  </div>
                                </div>
                              ) : null}
                            </div>
                          ))}
                      </div>
                      <p className="mt-4 text-xs text-sky-800">
                        Le 2 % précédemment détenu par Jérôme Martel est déjà intégré à Dany Bourassa / Gestion DB.
                      </p>
                    </div>

                    <div className="xl:col-span-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-5 shadow-sm">
                      <div className="mb-4 flex items-center justify-between gap-4">
                        <h4 className="text-base font-bold text-emerald-900">Transactions structurantes</h4>
                        <span className="rounded-full border border-emerald-200 bg-white px-3 py-1 text-xs font-bold text-emerald-700">
                          {scenario3Transactions.length} mouvements
                        </span>
                      </div>
                      <div className="space-y-3">
                        {scenario3Transactions.map((item) => (
                          <div key={item.id} className="rounded-xl border border-emerald-100 bg-white px-4 py-3">
                            <div className="mb-1 flex items-start justify-between gap-3">
                              <p className="text-sm font-semibold text-gray-900">#{item.id} · {item.vendeur}</p>
                              <span className="text-sm font-bold text-emerald-700">{item.percentage.toFixed(2)}%</span>
                            </div>
                            <p className="text-sm text-gray-700">→ {item.acheteur}</p>
                            <p className="mt-1 text-xs text-gray-500">{item.transaction}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="xl:col-span-4 rounded-2xl border border-indigo-200 bg-indigo-50 p-5 shadow-sm">
                      <div className="mb-4 flex items-center justify-between gap-4">
                        <h4 className="text-base font-bold text-indigo-900">Structure après transaction</h4>
                        <span className="rounded-full border border-indigo-200 bg-white px-3 py-1 text-xs font-bold text-indigo-700">
                          {scenario3Apres.length} blocs
                        </span>
                      </div>
                      <div className="space-y-2">
                        {[...scenario3Apres]
                          .sort((a, b) => (b.percentage - a.percentage) || a.name.localeCompare(b.name, 'fr'))
                          .map((item) => (
                          <div key={item.name} className="rounded-xl border border-indigo-100 bg-white px-4 py-3">
                            <div className="grid grid-cols-[minmax(0,1fr)_86px_86px] items-center gap-3">
                              <span className="text-sm font-semibold text-gray-900">{item.name}</span>
                              <span className={`text-right text-sm font-semibold ${item.variation.startsWith('+') ? 'text-green-700' : item.variation.startsWith('-') ? 'text-red-700' : 'text-gray-400'}`}>
                                {item.variation}
                              </span>
                              <span className="text-right text-sm font-bold text-indigo-700">{item.percentage.toFixed(2)}%</span>
                            </div>
                            {item.members?.length ? (
                              <div className="mt-3 rounded-xl border border-dashed border-indigo-200 bg-indigo-50/70 px-3 py-3">
                                <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-indigo-700">{item.detailLabel}</p>
                                <div className="space-y-1.5">
                                  {[...item.members]
                                    .sort((a, b) => (b.percentage - a.percentage) || a.name.localeCompare(b.name, 'fr'))
                                    .map((member) => (
                                    <div key={`${item.name}-${member.name}`} className="flex items-center justify-between gap-3 text-[12px] text-gray-700">
                                      <span className="truncate">{member.name}</span>
                                      <span className="font-semibold text-indigo-700">{member.percentage.toFixed(2)}%</span>
                                    </div>
                                    ))}
                                </div>
                                {item.detailNote ? <p className="mt-2 text-[11px] text-indigo-700">{item.detailNote}</p> : null}
                              </div>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                      <div className="mb-3 flex items-center justify-between gap-3">
                        <h4 className="text-base font-bold text-gray-900">Actionnaires finaux avant</h4>
                        <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-bold text-gray-700">{scenario3ConsolidatedAvantTotal.toFixed(2)}%</span>
                      </div>
                      <div className="space-y-2">
                        {[...scenario3ConsolidatedAvant]
                          .sort((a, b) => (b.percentage - a.percentage) || a.name.localeCompare(b.name, 'fr'))
                          .map((item) => (
                            <div key={`screen-consolidated-before-${item.name}`} className="flex items-center justify-between rounded-xl border border-gray-100 bg-gray-50 px-3 py-2 text-[12px]">
                              <span className="font-medium text-gray-900">{item.name}</span>
                              <span className="font-bold text-gray-700">{item.percentage.toFixed(2)}%</span>
                            </div>
                          ))}
                      </div>
                    </div>
                    <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                      <div className="mb-3 flex items-center justify-between gap-3">
                        <h4 className="text-base font-bold text-gray-900">Actionnaires finaux après</h4>
                        <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-bold text-gray-700">{scenario3ConsolidatedApresTotal.toFixed(2)}%</span>
                      </div>
                      <div className="space-y-2">
                        {[...scenario3ConsolidatedApres]
                          .sort((a, b) => (b.percentage - a.percentage) || a.name.localeCompare(b.name, 'fr'))
                          .map((item) => (
                            <div key={`screen-consolidated-after-${item.name}`} className="flex items-center justify-between rounded-xl border border-gray-100 bg-gray-50 px-3 py-2 text-[12px]">
                              <span className="font-medium text-gray-900">{item.name}</span>
                              <span className="font-bold text-gray-700">{item.percentage.toFixed(2)}%</span>
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
                  <div className="xl:col-span-3 space-y-4">
                    <div className="rounded-2xl border border-red-200 bg-red-50 p-5">
                      <div className="mb-3 flex items-center justify-between gap-3">
                        <h4 className="text-sm font-bold uppercase tracking-wide text-red-700">Vendeurs</h4>
                        <span className="rounded-full bg-white px-3 py-1 text-xs font-bold text-red-700 border border-red-200">
                          Total {totalVendeurs.toFixed(2)}%
                        </span>
                      </div>
                      <div className="space-y-3">
                        {vendeurs.map((vendeur) => (
                          <div key={vendeur.name} className="flex items-center justify-between rounded-xl bg-white/80 px-4 py-3">
                            <span className="text-sm font-medium text-gray-900">{vendeur.name}</span>
                            <span className="text-sm font-bold text-red-700">-{vendeur.montant.toFixed(2)}%</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="rounded-2xl border border-green-200 bg-green-50 p-5">
                      <div className="mb-3 flex items-center justify-between gap-3">
                        <h4 className="text-sm font-bold uppercase tracking-wide text-green-700">Acheteurs</h4>
                        <span className="rounded-full bg-white px-3 py-1 text-xs font-bold text-green-700 border border-green-200">
                          Total {totalAcheteurs.toFixed(2)}%
                        </span>
                      </div>
                      <div className="space-y-3">
                        {acheteurs.map((acheteur) => (
                          <div key={acheteur.name} className="flex items-center justify-between rounded-xl bg-white/80 px-4 py-3">
                            <span className="text-sm font-medium text-gray-900">{acheteur.name}</span>
                            <span className="text-sm font-bold text-green-700">+{acheteur.montant.toFixed(2)}%</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="xl:col-span-4">
                    <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                      <h4 className="mb-4 text-base font-bold text-gray-900">Structure d'achat</h4>
                      <div className="space-y-4">
                        {repartitionStructures.map((groupe) => (
                          <div key={groupe.structure} className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                            <div className="mb-3 flex items-center justify-between gap-3">
                              <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${getStructureBadgeClasses(groupe.structure)}`}>
                                {groupe.structure}
                              </span>
                              <span className="text-sm font-bold text-gray-900">{groupe.total.toFixed(2)}%</span>
                            </div>
                            <div className="space-y-2">
                              {groupe.items.map((item) => (
                                <div key={item.name} className="flex items-center justify-between rounded-xl bg-white px-3 py-2.5 text-[13px]">
                                  <span className="font-medium text-gray-900">{item.name}</span>
                                  <span className="font-semibold text-gray-700">{item.montant.toFixed(2)}%</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="xl:col-span-5">
                    <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                      <div className="mb-4 flex items-center justify-between gap-4">
                        <h4 className="text-base font-bold text-gray-900">Actionnariat final</h4>
                        <span className="rounded-full bg-indigo-50 px-3 py-1 text-xs font-bold text-indigo-700">
                          Total {totalFinal.toFixed(2)}%
                        </span>
                      </div>

                      <div className="space-y-2">
                        {actionnariatFinal.map((actionnaire) => (
                          <div
                            key={actionnaire.name}
                            className="grid grid-cols-[minmax(0,1fr)_90px_90px] items-center gap-3 rounded-xl border border-gray-100 px-4 py-3 hover:bg-gray-50"
                          >
                            <span className="truncate text-sm font-medium text-gray-900">{actionnaire.name}</span>
                            <span
                              className={`text-right text-sm font-semibold ${
                                actionnaire.delta > 0
                                  ? 'text-green-700'
                                  : actionnaire.delta < 0
                                    ? 'text-red-700'
                                    : 'text-gray-400'
                              }`}
                            >
                              {actionnaire.delta > 0 ? '+' : ''}
                              {actionnaire.delta.toFixed(2)}%
                            </span>
                            <span className="text-right text-sm font-bold text-indigo-700">
                              {actionnaire.percentage.toFixed(2)}%
                            </span>
                          </div>
                        ))}

                        <div className="mt-3 grid grid-cols-[minmax(0,1fr)_90px_90px] items-center gap-3 rounded-xl bg-gray-100 px-4 py-3 text-sm font-bold text-gray-900">
                          <span>Total</span>
                          <span className="text-right">—</span>
                          <span className="text-right">{totalFinal.toFixed(2)}%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              </>}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
    </>
  );
}
